import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { AnalysisResult, Trial } from '../types';

interface PowerLoadChartProps {
  result: AnalysisResult;
  trials: Trial[];
}

export const PowerLoadChart: React.FC<PowerLoadChartProps> = ({ result, trials }) => {
  const { powerDistribution } = result;
  
  if (!powerDistribution) return null;

  const data = trials.map((trial, index) => ({
    load: trial.load_kgf,
    power: powerDistribution.powerAtEachLoad[index] || 0,
    velocity: trial.v_max_ms
  }));

  const maxPower = Math.max(...powerDistribution.powerAtEachLoad);
  const optimalLoad = powerDistribution.optimalLoad;

  return (
    <div className="bg-theme-surface-1 border border-theme-border rounded-2xl p-6 shadow-card">
      <h3 className="text-2xl font-semibold tracking-tight text-theme-text mb-6">Power-Load Profile</h3>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="load" 
              stroke="#9CA3AF"
              label={{ value: 'Load (kg)', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: '#9CA3AF' } }}
            />
            <YAxis 
              stroke="#9CA3AF"
              label={{ value: 'Power (W)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#9CA3AF' } }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#1F2937', 
                border: '1px solid #374151', 
                borderRadius: '8px',
                color: '#F9FAFB'
              }}
              formatter={(value: number, name: string) => [
                name === 'power' ? `${value.toFixed(0)} W` : `${value.toFixed(1)} m/s`,
                name === 'power' ? 'Power' : 'Velocity'
              ]}
              labelFormatter={(label) => `Load: ${label} kg`}
            />
            <Line 
              type="monotone" 
              dataKey="power" 
              stroke="#60A5FA" 
              strokeWidth={3}
              dot={{ fill: '#60A5FA', strokeWidth: 2, r: 4 }}
            />
            <ReferenceLine 
              x={optimalLoad} 
              stroke="#F59E0B" 
              strokeDasharray="5 5"
              label={{ value: "Optimal Load", position: "top", style: { fill: '#F59E0B' } }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      <div className="mt-4 text-sm text-theme-muted">
        <p>Peak Power: <span className="text-theme-accent font-semibold">{maxPower.toFixed(0)} W</span> at <span className="text-theme-accent font-semibold">{optimalLoad.toFixed(1)} kg</span></p>
        <p>Power Bandwidth (90%): <span className="text-theme-accent font-semibold">{powerDistribution.powerAt90Range[0].toFixed(1)} - {powerDistribution.powerAt90Range[1].toFixed(1)} kg</span></p>
      </div>
    </div>
  );
};